import React, { useState } from "react";
import GrayCard from "components/gray-card";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import styled from "styled-components";
import Radio from "components/radio";
import { Link } from "react-router-dom";
import DragNDrop from "components/inputs/dragNdrop";
import { BytesToMB } from "utils/numberConvert";
import TextArea from "components/inputs/text-area";

interface IProps {
  data?: { isExplicit: boolean; text: string };
  setData: (data: { isExplicit: boolean; text: string }) => void;
}
const Lyrics = ({
  data = { isExplicit: false, text: "" },
  setData
}: IProps) => {
  const [addLyrics, setAddLyrics] = useState(data.text ? true : false);
  const [lyricType, setLyricType] = useState("clean");
  const [lyricFile, setLyricFile] = useState<File | undefined>();
  // const [lyricText, setLyricText] = useState(data?.text);

  return (
    <GrayCard title="Lyrics">
      <Typography weight="medium" mb="1.6" color={Colors.grey400}>
        Do you want to add lyrics?
      </Typography>
      <RadioContainer>
        <Radio value={addLyrics} onClick={() => setAddLyrics(true)} />
        <Typography color={Colors.white200} ml="0.4" mr="5">
          Yes
        </Typography>
        <Radio value={!addLyrics} onClick={() => setAddLyrics(false)} />
        <Typography color={Colors.white200} ml="0.4" mr="5">
          No
        </Typography>
      </RadioContainer>
      {addLyrics && (
        <>
          <Typography mb="0.8" weight="semibold">
            Lyric Requirements
          </Typography>
          <Typography size="s" color={Colors.grey500}>
            Lyrics must adhere to the following guidelines or they may be
            rejected:
          </Typography>
          <LyricsList>
            <li>
              <Typography size="s" mb="0.4">
                Lyrics must match the audio you’ve uploaded.
              </Typography>
            </li>
            <li>
              <Typography size="s" mb="0.4">
                Don’t add commas at the end of each of line. End each new line
                with no punctuation.
              </Typography>
            </li>
            <li>
              <Typography size="s" mb="0.4">
                Start each line with a capital letter. Don’t upload lyrics in
                all upper or lowercase
              </Typography>
            </li>
            <li>
              <Typography size="s" mb="0.4">
                Any explicit words or repeated lines must be included in full.
              </Typography>
            </li>
            <li>
              <Typography size="s">
                Lyrics should be formatted like a poem. Every individual lyric
                line should have a single break. Each verse, chorus, bridge etc
                should be seperated into stanzas using a double line break.
              </Typography>
            </li>
          </LyricsList>
          <Typography size="s" color={Colors.grey500} mb="2.4">
            Before uploading lyrics, please read{" "}
            <TextLink
              target="_blank"
              to={"https://artists.apple.com/support/1111-lyrics-guidelines "}
            >
              Apple Music’s full lyric guidelines
            </TextLink>
          </Typography>
          <AddLyricsContainer>
            <AddLyricsCol>
              <TextArea
                height="22.6rem"
                label="Add Lyrics"
                placeholder="Enter Lyrics"
                value={data.text}
                onChange={(e) => setData({ ...data, text: e.target.value })}
              />
            </AddLyricsCol>
            <AddLyricsCol>
              <Typography mb="0.4" weight="medium" color={Colors.grey400}>
                Or Upload Lyrics
              </Typography>
              <UploadArea>
                {!lyricFile && (
                  <DragNDrop
                    setFile={(file) => setLyricFile(file)}
                    type="doc"
                    height="100%"
                  />
                )}
                {lyricFile && (
                  <UploadedLyricsArea>
                    <Typography
                      size="xs"
                      color={Colors.white100}
                      weight="semibold"
                      mb="0.4"
                    >
                      {lyricFile.name}
                    </Typography>
                    <Typography
                      size="xs"
                      color={Colors.grey500}
                      weight="semibold"
                      mb="0.4"
                    >
                      {BytesToMB(lyricFile.size)}MB
                    </Typography>
                  </UploadedLyricsArea>
                )}
                {lyricFile && (
                  <CancelButton onClick={() => setLyricFile(undefined)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="11.9983"
                        cy="12.0003"
                        r="5.14286"
                        fill="#1E1E1E"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M22.0039 12C22.0039 17.5228 17.5267 22 12.0039 22C6.48106 22 2.00391 17.5228 2.00391 12C2.00391 6.47715 6.48106 2 12.0039 2C17.5267 2 22.0039 6.47715 22.0039 12ZM8.97354 8.96965C9.26643 8.67676 9.7413 8.67676 10.0342 8.96965L12.0039 10.9393L13.9735 8.96967C14.2664 8.67678 14.7413 8.67678 15.0342 8.96967C15.3271 9.26256 15.3271 9.73744 15.0342 10.0303L13.0645 12L15.0342 13.9696C15.3271 14.2625 15.3271 14.7374 15.0342 15.0303C14.7413 15.3232 14.2664 15.3232 13.9735 15.0303L12.0039 13.0607L10.0342 15.0303C9.74133 15.3232 9.26645 15.3232 8.97356 15.0303C8.68067 14.7374 8.68067 14.2625 8.97356 13.9697L10.9432 12L8.97354 10.0303C8.68064 9.73742 8.68064 9.26254 8.97354 8.96965Z"
                        fill="#CDCDCD"
                      />
                    </svg>
                  </CancelButton>
                )}
              </UploadArea>
            </AddLyricsCol>
          </AddLyricsContainer>
        </>
      )}
      <Typography color={Colors.grey400} weight="medium">
        Do these lyrics contain explicit content?
      </Typography>
      <LyricRadio>
        <Radio
          value={lyricType === "explicit"}
          onClick={() => {
            setLyricType("explicit");
            setData({ ...data, isExplicit: true });
          }}
        />
        <Typography color={Colors.white200} ml="0.4" size="s">
          Explicit Content (This track includes profanity or offensive language)
        </Typography>
      </LyricRadio>
      <LyricRadio>
        <Radio
          value={lyricType === "clean"}
          onClick={() => {
            setLyricType("clean");
            setData({ ...data, isExplicit: false });
          }}
        />
        <Typography color={Colors.white200} ml="0.4" size="s">
          Clean (There is another version, that contains profanity or offensive
          language and this is the clean version)
        </Typography>
      </LyricRadio>
      <LyricRadio>
        <Radio
          value={lyricType === "not-explicit"}
          onClick={() => {
            setLyricType("not-explicit");
            setData({ ...data, isExplicit: false });
          }}
        />
        <Typography color={Colors.white200} ml="0.4" size="s">
          Not Explicit (This track does NOT include profanity or offensive
          language)
        </Typography>
      </LyricRadio>
    </GrayCard>
  );
};

export default Lyrics;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2.4rem;
`;

const LyricsList = styled.ul`
  color: ${Colors.grey500};
  padding-left: 1.5rem;
  margin-top: 0.5rem;
  width: 62%;
  margin-bottom: 0.5rem;

  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const AddLyricsContainer = styled.div`
  display: grid;
  align-items: center;
  justify-content: space-between;
  grid-template-columns: 60% 38%;
  margin-bottom: 2.4rem;
  @media only screen and (max-width: 769px) {
    grid-template-columns: 100%;
    grid-row-gap: 0.8rem;
  }
`;

const AddLyricsCol = styled.div``;

const TextLink = styled(Link)`
  text-decoration: underline;
  color: ${Colors.yellow400};
  cursor: pointer;
`;

const UploadArea = styled.div`
  width: 100%;
  position: relative;
  height: 24.6rem;
`;

const UploadedLyricsArea = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${Colors.grey600};
  border-radius: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
const CancelButton = styled.div`
  position: absolute;
  cursor: pointer;
  top: -1.2rem;
  right: -1.2rem;
  width: 2.4rem;
  height: 2.4rem;
`;

const LyricRadio = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1.6rem;
  @media only screen and (max-width: 769px) {
    display: grid;
    grid-template-columns: 5% 95%;
  }
`;
